import { supabase } from './supabaseService';
import { logger } from '../utils/logger';

// ---- Types ----

export interface KnowledgeSource {
  id: string;
  client_id: string;
  project_id: string | null;
  source_type: SourceType;
  source_origin: string;
  title: string;
  meeting_date: string | null;
  external_id: string | null;
  raw_text: string;
  file_url: string | null;
  file_name: string | null;
  file_type: string | null;
  summary: string | null;
  speaker_info: SpeakerInfo[];
  metadata: Record<string, any>;
  chunk_count: number;
  ingestion_status: 'pending' | 'processing' | 'completed' | 'failed';
  ingestion_error: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export type SourceType =
  | 'fathom_meeting'
  | 'elevenlabs_call'
  | 'elevenlabs_whatsapp'
  | 'manual_transcript'
  | 'internal_note'
  | 'email'
  | 'document'
  | 'contract'
  | 'budget'
  | 'proposal';

export interface SpeakerInfo {
  name: string;
  role?: string;
  email?: string;
}

export interface ChatMessage {
  id: string;
  chat_id: string;
  role: 'user' | 'assistant';
  content: string;
  confidence?: 'high' | 'medium' | 'low';
  evidence?: Evidence[];
  filters_used?: ChatFilters;
  tokens_used?: number;
  model?: string;
  created_at: string;
}

export interface Evidence {
  source_id: string;
  source_title: string;
  source_type: string;
  date: string | null;
  speaker: string | null;
  start_time_seconds: number | null;
  end_time_seconds: number | null;
  quote: string;
  relevance: number;
}

export type SearchScope = 'client_all' | 'client_general' | 'project_only' | 'project_with_general';

export interface ChatFilters {
  source_type?: SourceType;
  date_from?: string;
  date_to?: string;
}

export interface IntelligenceChat {
  id: string;
  client_id: string;
  project_id: string | null;
  title: string;
  created_by: string;
  message_count: number;
  created_at: string;
  updated_at: string;
}

export interface ChatResponse {
  answer: string;
  confidence: 'high' | 'medium' | 'low';
  evidence: Evidence[];
  chat_id: string;
  tokens_used: number;
  chunks_found: number;
}

export interface AddSourceInput {
  client_id: string;
  project_id?: string;
  source_type: SourceType;
  title: string;
  meeting_date?: string;
  raw_text: string;
  speaker_info?: SpeakerInfo[];
}

// ---- Service ----

class ClientIntelligenceService {

  // === Sources ===

  async getSources(clientId: string, projectId?: string, scope?: SearchScope): Promise<KnowledgeSource[]> {
    try {
      let query = supabase
        .from('client_knowledge_sources')
        .select('*')
        .eq('client_id', clientId);

      if (scope === 'client_general') {
        query = query.is('project_id', null);
      } else if (scope === 'project_only' && projectId) {
        query = query.eq('project_id', projectId);
      } else if (scope === 'project_with_general' && projectId) {
        query = query.or(`project_id.eq.${projectId},project_id.is.null`);
      }
      // 'client_all' or default → no project filter

      const { data, error } = await query.order('meeting_date', { ascending: false, nullsFirst: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error getting knowledge sources', error);
      return [];
    }
  }

  async addSource(input: AddSourceInput): Promise<KnowledgeSource | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('client_knowledge_sources')
        .insert({
          client_id: input.client_id,
          project_id: input.project_id || null,
          source_type: input.source_type,
          source_origin: 'manual',
          title: input.title,
          meeting_date: input.meeting_date || null,
          raw_text: input.raw_text,
          speaker_info: input.speaker_info || [],
          created_by: user.id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error('Error adding knowledge source', error);
      throw error;
    }
  }

  /** Upload a file (PDF, DOCX, TXT), extract text, create source and ingest */
  async addFileSource(
    clientId: string,
    file: File,
    sourceType: SourceType,
    title: string,
    projectId?: string,
    meetingDate?: string
  ): Promise<KnowledgeSource> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // 1. Upload file to Supabase Storage
    const filePath = `knowledge/${clientId}/${Date.now()}_${file.name}`;
    const { error: uploadError } = await supabase.storage
      .from('documents')
      .upload(filePath, file, { upsert: false });

    if (uploadError) throw new Error(`Error subiendo archivo: ${uploadError.message}`);

    const { data: urlData } = supabase.storage.from('documents').getPublicUrl(filePath);
    const fileUrl = urlData.publicUrl;

    // 2. Extract text from file
    let rawText = '';
    const mimeType = file.type;

    if (mimeType === 'text/plain' || file.name.endsWith('.txt') || file.name.endsWith('.md')) {
      rawText = await file.text();
    } else if (mimeType === 'text/csv' || file.name.endsWith('.csv')) {
      rawText = await file.text();
    } else {
      // For PDF, DOCX and other binary formats: read as text and send to Edge Function
      // The Edge Function will handle extraction via Claude
      rawText = await this.extractTextFromFile(file);
    }

    if (!rawText || rawText.trim().length < 20) {
      throw new Error('No se pudo extraer texto del archivo. Intenta con un archivo de texto o pega el contenido manualmente.');
    }

    // 3. Create source
    const { data, error } = await supabase
      .from('client_knowledge_sources')
      .insert({
        client_id: clientId,
        project_id: projectId || null,
        source_type: sourceType,
        source_origin: 'manual',
        title,
        meeting_date: meetingDate || null,
        raw_text: rawText,
        file_url: fileUrl,
        file_name: file.name,
        file_type: mimeType,
        speaker_info: [],
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;

    // 4. Trigger ingestion
    this.ingestSource(data.id).catch(err =>
      logger.error('Background ingestion failed', err)
    );

    return data;
  }

  /** Extract text from a file using Claude via Edge Function */
  private async extractTextFromFile(file: File): Promise<string> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    // Read file as base64
    const buffer = await file.arrayBuffer();
    const base64 = btoa(
      new Uint8Array(buffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
    );

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const response = await fetch(
      `${supabaseUrl}/functions/v1/ingest-knowledge-source`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          extract_text: true,
          file_base64: base64,
          file_name: file.name,
          mime_type: file.type,
        }),
      }
    );

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err.error || 'Error extrayendo texto');
    }

    const result = await response.json();
    return result.text || '';
  }

  async deleteSource(sourceId: string): Promise<void> {
    const { error } = await supabase
      .from('client_knowledge_sources')
      .delete()
      .eq('id', sourceId);

    if (error) throw error;
  }

  /** Trigger ingestion for a source via Edge Function */
  async ingestSource(sourceId: string): Promise<void> {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(
        `${supabaseUrl}/functions/v1/ingest-knowledge-source`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ source_id: sourceId }),
        }
      );

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || `Ingestion failed: ${response.status}`);
      }
    } catch (error) {
      logger.error('Error ingesting source', error);
      throw error;
    }
  }

  /** Add source and immediately trigger ingestion */
  async addAndIngest(input: AddSourceInput): Promise<KnowledgeSource> {
    const source = await this.addSource(input);
    if (!source) throw new Error('Failed to create source');

    // Trigger ingestion (non-blocking)
    this.ingestSource(source.id).catch(err =>
      logger.error('Background ingestion failed', err)
    );

    return source;
  }

  // === Chat ===

  async sendMessage(
    clientId: string,
    question: string,
    chatId?: string,
    projectId?: string,
    filters?: ChatFilters,
    scope?: SearchScope
  ): Promise<ChatResponse> {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const response = await fetch(
      `${supabaseUrl}/functions/v1/client-intelligence-chat`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          client_id: clientId,
          question,
          chat_id: chatId || null,
          project_id: projectId || null,
          scope: scope || null,
          filters: filters || null,
        }),
      }
    );

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.error || `Chat failed: ${response.status}`);
    }

    return await response.json();
  }

  // === Chat History ===

  async getChats(clientId: string, projectId?: string): Promise<IntelligenceChat[]> {
    try {
      let query = supabase
        .from('client_intelligence_chats')
        .select('*')
        .eq('client_id', clientId);

      if (projectId) {
        query = query.or(`project_id.eq.${projectId},project_id.is.null`);
      }

      const { data, error } = await query.order('updated_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error getting intelligence chats', error);
      return [];
    }
  }

  async getChatMessages(chatId: string): Promise<ChatMessage[]> {
    try {
      const { data, error } = await supabase
        .from('client_intelligence_messages')
        .select('*')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      return data || [];
    } catch (error) {
      logger.error('Error getting chat messages', error);
      return [];
    }
  }

  async deleteChat(chatId: string): Promise<void> {
    const { error } = await supabase
      .from('client_intelligence_chats')
      .delete()
      .eq('id', chatId);

    if (error) throw error;
  }

  // === Fathom Integration ===

  /** Check if a Fathom recording is already linked to any client */
  async isRecordingLinked(recordingId: number): Promise<boolean> {
    const { data } = await supabase
      .from('client_knowledge_sources')
      .select('id')
      .eq('source_type', 'fathom_meeting')
      .eq('external_id', String(recordingId))
      .limit(1);

    return (data?.length || 0) > 0;
  }

  /** Get all linked recording IDs for quick lookup */
  async getLinkedRecordingIds(): Promise<Set<number>> {
    const { data } = await supabase
      .from('client_knowledge_sources')
      .select('external_id')
      .eq('source_type', 'fathom_meeting')
      .not('external_id', 'is', null);

    return new Set((data || []).map(d => parseInt(d.external_id)));
  }

  /** Auto-match a Fathom meeting's invitees to CRM contacts by email */
  async findMatchingContacts(
    inviteeEmails: string[]
  ): Promise<{ id: string; full_name: string; email: string; company: string | null }[]> {
    if (!inviteeEmails.length) return [];

    // Filter out common internal/generic domains
    const validEmails = inviteeEmails.filter(e => e && e.includes('@'));
    if (!validEmails.length) return [];

    const { data, error } = await supabase
      .from('contacts')
      .select('id, full_name, email, company')
      .in('email', validEmails);

    if (error) {
      logger.error('Error finding matching contacts', error);
      return [];
    }

    return data || [];
  }

  /** Link a Fathom meeting to a client: fetch transcript, create source, ingest */
  async linkFathomMeeting(
    clientId: string,
    meeting: {
      recording_id: number;
      title: string;
      recording_start_time: string;
      url: string;
      calendar_invitees: { name: string | null; email: string | null; is_external: boolean }[];
      recorded_by: { name: string; email: string };
    },
    projectId?: string
  ): Promise<KnowledgeSource> {
    // Check if already linked
    const alreadyLinked = await this.isRecordingLinked(meeting.recording_id);
    if (alreadyLinked) {
      throw new Error('Esta reunión ya está vinculada a un cliente');
    }

    // Fetch transcript from Fathom
    const { fathomService } = await import('./fathomService');
    let rawText = '';
    let speakerInfo: SpeakerInfo[] = [];

    try {
      const transcript = await fathomService.getTranscript(String(meeting.recording_id));

      if (Array.isArray(transcript) && transcript.length > 0) {
        rawText = transcript
          .map((item: any) => `[${item.timestamp}] ${item.speaker?.display_name || 'Unknown'}: ${item.text}`)
          .join('\n');

        const speakers = [...new Set(transcript.map((t: any) => t.speaker?.display_name).filter(Boolean))];
        speakerInfo = speakers.map(name => ({ name: name as string }));
      }
    } catch (err) {
      logger.warn('Could not fetch Fathom transcript, using meeting metadata', err);
    }

    // If no transcript, use meeting info as content
    if (!rawText) {
      const participants = meeting.calendar_invitees
        .map(i => `${i.name || 'Sin nombre'} (${i.email || 'sin email'})${i.is_external ? ' [externo]' : ''}`)
        .join('\n');

      rawText = [
        `Reunión: ${meeting.title}`,
        `Fecha: ${new Date(meeting.recording_start_time).toLocaleString('es-ES')}`,
        `Grabado por: ${meeting.recorded_by.name} (${meeting.recorded_by.email})`,
        `Participantes:\n${participants}`,
        '',
        'Nota: No se pudo obtener la transcripción completa de esta reunión.',
        `Ver en Fathom: ${meeting.url}`,
      ].join('\n');

      speakerInfo = meeting.calendar_invitees
        .filter(i => i.name)
        .map(i => ({ name: i.name!, email: i.email || undefined }));
    }

    // Create source and ingest
    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase
      .from('client_knowledge_sources')
      .insert({
        client_id: clientId,
        project_id: projectId || null,
        source_type: 'fathom_meeting',
        source_origin: 'fathom_api',
        title: meeting.title,
        meeting_date: meeting.recording_start_time,
        external_id: String(meeting.recording_id),
        raw_text: rawText,
        speaker_info: speakerInfo,
        metadata: {
          fathom_url: meeting.url,
          recorded_by: meeting.recorded_by,
          invitees: meeting.calendar_invitees,
        },
        created_by: user?.id,
      })
      .select()
      .single();

    if (error) throw error;

    // Trigger ingestion
    this.ingestSource(data.id).catch(err =>
      logger.error('Background ingestion of Fathom meeting failed', err)
    );

    return data;
  }

  /** Get Fathom meetings for a specific client (by matching invitee emails) */
  async getFathomMeetingsForClient(
    clientEmail: string
  ): Promise<any[]> {
    const { fathomService } = await import('./fathomService');
    if (!fathomService.isConfigured()) return [];

    try {
      const meetings = await fathomService.getMeetings();
      return meetings.filter(m =>
        m.calendar_invitees?.some((inv: any) =>
          inv.email?.toLowerCase() === clientEmail.toLowerCase()
        )
      );
    } catch {
      return [];
    }
  }
}

export const clientIntelligenceService = new ClientIntelligenceService();
